/**
 * Financial Statements — the P&L, one entity, a range of months. Phase 5, the first slice.
 *
 * Every figure comes from `GET /api/finance/pnl`, which runs `profitAndLoss` in packages/finance
 * over `derived.statement_line`; this screen adds nothing up. The columns are months, the rows
 * are canonical lines under their statement section, and the bottom rows are the section totals,
 * NOI, free cash flow and net — all ACTUAL (FINANCE.md §8.1). The money nobody has classified is
 * the last row, PENDING CLASSIFICATION, shown with its amount so a statement is never quietly
 * short; a month with any pending money says so in its header.
 *
 * The range defaults to the last six months including this one, and the picker is two month
 * fields; the API refuses more than 24 months (`PNL_MAX_MONTHS`).
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type BalanceSheetResponse,
  type CashFlowFigures,
  type PnlFigures,
  type PnlResponse,
  type Property,
  type TieOutResponse,
  type TieOutStatus,
} from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

const SECTION_LABEL: Record<string, string> = {
  operating_revenue: 'Operating revenue',
  operating_expense: 'Operating expense',
  debt_service: 'Debt service',
  capex: 'Capital expenditure',
  non_operating: 'Non-operating',
  equity_movement: 'Equity movement',
  balance_sheet: 'Balance sheet',
};

/** YYYY-MM for a date, in local time — the month the owner is looking at. */
function yearMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthsAgo(n: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return yearMonth(d);
}

export function StatementsPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();
  const [from, setFrom] = useState(() => monthsAgo(5));
  const [to, setTo] = useState(() => monthsAgo(0));
  // One property, or the whole entity (every line, including those on no property). The
  // balance sheet is the entity's regardless: QuickBooks keeps balances per company file.
  const [propertyId, setPropertyId] = useState<string>('');

  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });
  const propertyQuery = propertyId === '' ? '' : `&property=${propertyId}`;

  const report = useQuery({
    queryKey: ['finance', 'pnl', entityId, from, to, propertyId],
    enabled: entityId !== null && from <= to,
    queryFn: () =>
      api.get<PnlResponse>(
        `/api/finance/pnl?entity=${entityId}&from=${from}&to=${to}${propertyQuery}`,
      ),
  });

  const balance = useQuery({
    queryKey: ['finance', 'balance-sheet', entityId],
    enabled: entityId !== null,
    queryFn: () => api.get<BalanceSheetResponse>(`/api/finance/balance-sheet?entity=${entityId}`),
  });

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = report.data;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Financial Statements</h1>
          <p className="subtitle">
            Profit &amp; loss from the mirror&apos;s classified lines. Every figure is ACTUAL and
            computed by code; what is not yet classified is shown beside it, never inside it.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
        <label>
          From{' '}
          <input
            type="month"
            value={from}
            max={to}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label>
          To{' '}
          <input
            type="month"
            value={to}
            min={from}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
        <label>
          Property{' '}
          <select value={propertyId} onChange={(event) => setPropertyId(event.target.value)}>
            <option value="">Whole entity</option>
            {(properties.data?.properties ?? [])
              .filter((p) => p.entity_id === entityId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      {(report.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {report.isError && (
        <p className="error">Could not load the statement: {report.error.message}</p>
      )}

      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && <PnlTable data={data} />}

      {data && <CashFlowCard data={data} />}

      {propertyId !== '' && (
        <p className="muted small">
          The P&amp;L and cash flow above are one property&apos;s lines. The balance sheet below is
          the entity&apos;s: QuickBooks keeps balances per company file, not per property.
        </p>
      )}

      {balance.data && <BalanceSheetCard data={balance.data} />}

      {entityId !== null && <TieOutCard entityId={entityId} />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

const TIE_OUT_CHIP: Record<TieOutStatus, string> = {
  tied: 'chip chip-ok',
  explained: 'chip chip-warn',
  unexplained: 'chip chip-attention',
};

const TIE_OUT_WORDS: Record<TieOutStatus, string> = {
  tied: 'Ties to the cent',
  explained: 'Differs, and every difference is explained',
  unexplained: 'Differs, and not every difference is explained',
};

const REASON_WORDS: Record<string, string> = {
  transfers_excluded: 'intercompany transfer, excluded here by design',
  reclassified_not_in_quickbooks: 'approved reclassification, not yet in QuickBooks',
};

/**
 * The QuickBooks tie-out: this entity's month against QuickBooks' own Profit and Loss report,
 * account by account (Phase 5's Definition of Done). Every figure and every verdict is the API's
 * (`pnlTieOut` in packages/finance); this card adds nothing up. It compares against a report that
 * was fetched and stored, and says when — it never calls QuickBooks itself.
 */
function TieOutCard({ entityId }: { entityId: string }): ReactNode {
  const [month, setMonth] = useState(() => monthsAgo(1));
  const [basis, setBasis] = useState<'Accrual' | 'Cash'>('Accrual');
  const query = useQuery({
    queryKey: ['finance', 'tie-out', entityId, month, basis],
    queryFn: () =>
      api.get<TieOutResponse>(
        `/api/finance/tie-out?entity=${entityId}&month=${month}&basis=${basis}`,
      ),
  });
  const data = query.data;
  const t = data?.tieOut;
  const queryClient = useQueryClient();
  const fetchFromQuickBooks = useMutation({
    mutationFn: () =>
      api.post<{ inserted: boolean }>('/api/finance/tie-out/fetch', {
        entity: entityId,
        month,
        basis,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['finance', 'tie-out', entityId, month, basis] }),
  });

  return (
    <section className="card">
      <h2>QuickBooks tie-out</h2>
      <div className="filters">
        <label>
          Month{' '}
          <input type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
        </label>
        <label>
          Basis{' '}
          <select
            value={basis}
            onChange={(event) => setBasis(event.target.value as 'Accrual' | 'Cash')}
          >
            <option value="Accrual">Accrual</option>
            <option value="Cash">Cash</option>
          </select>
        </label>
      </div>
      {query.isLoading && <p className="muted">Loading…</p>}
      {query.isError && <p className="error">Could not load the tie-out: {query.error.message}</p>}
      {data && data.entity.realmId === null && (
        <p className="muted">
          This entity has no QuickBooks company file connected, so there is nothing to tie out to.
        </p>
      )}
      {data && data.entity.realmId !== null && (
        <p className="small">
          <button
            type="button"
            disabled={fetchFromQuickBooks.isPending}
            onClick={() => fetchFromQuickBooks.mutate()}
          >
            {fetchFromQuickBooks.isPending ? 'Reading QuickBooks…' : 'Fetch from QuickBooks'}
          </button>{' '}
          <span className="muted">
            Reads this month&apos;s P&amp;L report and counts its documents by type. Read-only.
          </span>
        </p>
      )}
      {fetchFromQuickBooks.isError && <p className="error">{fetchFromQuickBooks.error.message}</p>}
      {data && data.entity.realmId !== null && !t && (
        <p className="muted">
          No QuickBooks {basis} P&amp;L is stored for {data.window.startDate} to{' '}
          {data.window.endDate} yet; nothing is compared against zero.
        </p>
      )}
      {data?.report && t && (
        <>
          <p className="small">
            <span className={TIE_OUT_CHIP[t.status]}>{TIE_OUT_WORDS[t.status]}</span>{' '}
            <span className="muted">
              {t.counts.tied} tied · {t.counts.explained} explained · {t.counts.unexplained}{' '}
              unexplained. QuickBooks report fetched{' '}
              {new Date(data.report.fetchedAt).toLocaleString()}, {data.report.basis} basis.
            </span>
          </p>
          <table className="table compact">
            <thead>
              <tr>
                <th>Account</th>
                <th>Section</th>
                <th className="num">QuickBooks</th>
                <th className="num">Sixgen</th>
                <th className="num">Difference</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {t.lines.map((line) => (
                <tr key={line.accountId}>
                  <td>{line.name}</td>
                  <td className="muted">{line.section}</td>
                  <td className="num">{formatMoney(line.reportedCents)}</td>
                  <td className="num">{formatMoney(line.oursCents)}</td>
                  <td className="num">
                    {line.differenceCents === '0' ? '—' : formatMoney(line.differenceCents)}
                  </td>
                  <td>
                    <span className={TIE_OUT_CHIP[line.status]}>{line.status}</span>{' '}
                    <span className="muted small">
                      {line.reasons.map((r) => REASON_WORDS[r]).join('; ')}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="pnl-figure pnl-net">
                <th scope="row" colSpan={2}>
                  Net income
                </th>
                <td className="num">{formatMoney(t.reportedNetIncomeCents)}</td>
                <td className="num">{formatMoney(t.oursNetIncomeCents)}</td>
                <td className="num">
                  {t.netDifferenceCents === '0' ? '—' : formatMoney(t.netDifferenceCents)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
          {t.counts.unexplained > 0 && (
            <p className="muted small">
              Unexplained means the mirror does not hold what QuickBooks reports for that account,
              even before Sixgen&apos;s own decisions are applied — most often a transaction type
              the mirror does not import yet (it reads purchases and deposits; QuickBooks&apos;
              P&amp;L also counts invoices, sales receipts, bills and journal entries).
            </p>
          )}
          {t.unassigned.lineCount > 0 && (
            <p className="muted small">
              {t.unassigned.lineCount} {t.unassigned.lineCount === 1 ? 'line has' : 'lines have'} no
              account at all ({formatMoney(t.unassigned.signedCents)}), so{' '}
              {t.unassigned.lineCount === 1 ? 'it is' : 'they are'} on neither P&amp;L.
            </p>
          )}
          {data.census && (
            <p className="muted small">
              QuickBooks documents this month, by type (counted{' '}
              {new Date(data.census.fetchedAt).toLocaleString()}):{' '}
              {Object.entries(data.census.counts)
                .filter(([, count]) => count > 0)
                .map(
                  ([type, count]) =>
                    `${type} ${count}${data.census!.imported.includes(type) ? '' : ' (not imported)'}`,
                )
                .join(' · ')}
              .
            </p>
          )}
          <p className="muted small">
            {t.label}. Sixgen&apos;s figure is the account a person approved, with paired
            intercompany transfers left out; QuickBooks&apos; is its own report. Both in each
            account&apos;s normal balance: income and expense positive.
          </p>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------

/**
 * The cash flow statement: the same rows as the P&L above, arranged by activity — operating,
 * investing, financing — with the net change, per month and in total. Direct method over the
 * mirror's cash-basis lines, as the API says; not a bank reconciliation.
 */
function CashFlowCard({ data }: { data: PnlResponse }): ReactNode {
  const columns = [...data.months.map((m) => m.cash_flow), data.cash_flow_total];
  const rows: Array<[string, (c: CashFlowFigures) => string]> = [
    ['Operating activities', (c) => c.operating_cents],
    ['Investing activities (CapEx)', (c) => c.investing_cents],
    ['Financing activities (debt service, owners)', (c) => c.financing_cents],
  ];
  return (
    <section className="card">
      <h2>Cash flow statement</h2>
      <p className="muted small">
        {data.cash_flow_total.label} · the P&amp;L&apos;s lines arranged by activity; net change is
        the three summed. Movements between the entity&apos;s own balance-sheet accounts sit outside
        it. Not a bank reconciliation.
      </p>
      <div className="table-wrap">
        <table className="table pnl">
          <thead>
            <tr>
              <th>Activity</th>
              {data.months.map((m) => (
                <th key={m.month} className="num">
                  {m.month}
                </th>
              ))}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, pick]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                {columns.map((c, i) => (
                  <td key={i} className="num">
                    {formatMoney(pick(c))}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="pnl-figure pnl-net">
              <th scope="row">Net change in cash</th>
              {columns.map((c, i) => (
                <td key={i} className="num">
                  {formatMoney(c.net_change_cents)}
                </td>
              ))}
            </tr>
            <tr className="muted">
              <th scope="row">Between own balance-sheet accounts (outside)</th>
              {columns.map((c, i) => (
                <td key={i} className="num">
                  {formatMoney(c.balance_sheet_cents)}
                </td>
              ))}
            </tr>
            <tr className="pnl-pending">
              <th scope="row">PENDING CLASSIFICATION · beside the statement</th>
              {columns.map((c, i) => (
                <td key={i} className="num" title={`${c.pending_line_count} lines`}>
                  {c.pending_line_count === 0 ? '—' : formatMoney(c.pending_cents)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

const TYPE_LABEL: Record<string, string> = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
};

/**
 * The balance sheet as QuickBooks last reported it: every asset, liability and equity account
 * with its balance, and the three totals. The difference is shown, named — on a healthy file it
 * is the net income QuickBooks has not yet closed to retained earnings — never folded into
 * equity to make the sheet tidy.
 */
function BalanceSheetCard({ data }: { data: BalanceSheetResponse }): ReactNode {
  const { sheet, lines } = data;
  const types = ['asset', 'liability', 'equity'] as const;
  return (
    <section className="card">
      <h2>Balance sheet</h2>
      <p className="muted small">
        {sheet.label} · every balance as QuickBooks last reported it
        {data.balances_as_of
          ? `, last carried ${new Date(data.balances_as_of).toLocaleString()}`
          : ' — no balance carried yet; run a sync'}
        . {sheet.accounts} active {sheet.accounts === 1 ? 'account' : 'accounts'}
        {sheet.unknown_balances > 0 &&
          `, ${sheet.unknown_balances} with no reported balance, not summed`}
        . Liabilities are shown as owed and equity as contributed.
      </p>
      {lines.length === 0 ? (
        <p className="muted">No balance-sheet accounts in the mirror for this entity.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Type</th>
              <th className="num">Balance</th>
            </tr>
          </thead>
          <tbody>
            {types.map((type) => (
              <BalanceRows
                key={type}
                type={type}
                lines={lines.filter((l) => l.account_type === type)}
                totalCents={
                  type === 'asset'
                    ? sheet.assets_cents
                    : type === 'liability'
                      ? sheet.liabilities_cents
                      : sheet.equity_cents
                }
              />
            ))}
            <tr className="pnl-figure pnl-net">
              <th scope="row" colSpan={2}>
                {sheet.balanced
                  ? 'Assets equal liabilities plus equity'
                  : 'Assets less liabilities and equity (net income not yet closed, per QuickBooks)'}
              </th>
              <td className="num">{formatMoney(sheet.unclosed_cents)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
}

function BalanceRows({
  type,
  lines,
  totalCents,
}: {
  type: 'asset' | 'liability' | 'equity';
  lines: BalanceSheetResponse['lines'];
  totalCents: string;
}): ReactNode {
  return (
    <>
      <tr className="pnl-section">
        <th scope="rowgroup" colSpan={3}>
          {TYPE_LABEL[type]}
        </th>
      </tr>
      {lines.map((l) => (
        <tr key={l.account_id} className={l.is_active ? '' : 'muted'}>
          <td>
            {l.name}
            {!l.is_active && <span className="chip-tag">inactive</span>}
          </td>
          <td className="muted">{l.account_subtype ?? '—'}</td>
          <td className="num">
            {l.display_cents === null ? (
              <span className="muted">not reported</span>
            ) : (
              formatMoney(l.display_cents)
            )}
          </td>
        </tr>
      ))}
      <tr className="pnl-subtotal">
        <th scope="row" colSpan={2}>
          {TYPE_LABEL[type]} total
        </th>
        <td className="num">{formatMoney(totalCents)}</td>
      </tr>
    </>
  );
}

// ---------------------------------------------------------------------------------------------

function PnlTable({ data }: { data: PnlResponse }): ReactNode {
  const months = data.months;
  const columns = [...months.map((m) => m.pnl), data.total];
  // Every canonical line any month or the total carries, in the total's order (statement order),
  // then any line that appears only in one month — which cannot happen, since the total sums the
  // same rows, but the union costs nothing and never drops a row.
  const lineOrder = new Map<string, string>();
  for (const l of data.total.lines) lineOrder.set(l.key, l.section);
  for (const m of months)
    for (const l of m.pnl.lines) if (!lineOrder.has(l.key)) lineOrder.set(l.key, l.section);
  const sections = [...new Set([...lineOrder.values()])];
  const amountOf = (pnl: PnlFigures, key: string): string =>
    pnl.lines.find((l) => l.key === key)?.amount_cents ?? '0';

  if (data.total.lines.length === 0 && data.total.pending_line_count === 0) {
    return (
      <div className="card empty">
        <h2>Nothing on the books for these months.</h2>
        <p>
          Lines arrive from the QuickBooks connector — <code>pnpm qbo:sync</code> — and reach the
          statement once their account is mapped onto Sixgen&apos;s chart.
        </p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="table pnl">
        <thead>
          <tr>
            <th>Line</th>
            {months.map((m) => (
              <th key={m.month} className="num">
                {m.month}
                {!m.pnl.complete && (
                  <span
                    className="chip chip-warn"
                    title={`${m.pnl.pending_line_count} lines not yet classified`}
                  >
                    pending
                  </span>
                )}
              </th>
            ))}
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((section) => (
            <SectionRows
              key={section}
              section={section}
              keys={[...lineOrder.entries()].filter(([, s]) => s === section).map(([k]) => k)}
              columns={columns}
              amountOf={amountOf}
            />
          ))}
          <tr className="pnl-figure">
            <th scope="row">NOI</th>
            {columns.map((pnl, i) => (
              <td key={i} className="num">
                {formatMoney(pnl.noi_cents)}
              </td>
            ))}
          </tr>
          <tr className="pnl-figure muted">
            <th scope="row">NOI margin</th>
            {columns.map((pnl, i) => (
              <td key={i} className="num">
                {pnl.noi_margin === null ? '—' : `${(pnl.noi_margin * 100).toFixed(1)}%`}
              </td>
            ))}
          </tr>
          <tr className="pnl-figure">
            <th scope="row">Free cash flow</th>
            {columns.map((pnl, i) => (
              <td key={i} className="num">
                {formatMoney(pnl.free_cash_flow_cents)}
              </td>
            ))}
          </tr>
          <tr className="pnl-figure pnl-net">
            <th scope="row">Net</th>
            {columns.map((pnl, i) => (
              <td key={i} className="num">
                {formatMoney(pnl.net_cents)}
              </td>
            ))}
          </tr>
          <tr className="pnl-pending">
            <th scope="row">
              {data.total.pending_label}
              <span className="muted small"> · beside the statement, not in it</span>
            </th>
            {columns.map((pnl, i) => (
              <td key={i} className="num" title={`${pnl.pending_line_count} lines`}>
                {pnl.pending_line_count === 0 ? '—' : formatMoney(pnl.pending_cents)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <p className="muted small">
        {data.total.label} · {data.total.calc_method} · as of{' '}
        {new Date(data.as_of).toLocaleString()}
      </p>
    </div>
  );
}

function SectionRows({
  section,
  keys,
  columns,
  amountOf,
}: {
  section: string;
  keys: string[];
  columns: PnlFigures[];
  amountOf: (pnl: PnlFigures, key: string) => string;
}): ReactNode {
  return (
    <>
      <tr className="pnl-section">
        <th scope="rowgroup" colSpan={columns.length + 1}>
          {SECTION_LABEL[section] ?? section}
        </th>
      </tr>
      {keys.map((key) => (
        <tr key={key}>
          <td>
            <code>{key}</code>
          </td>
          {columns.map((pnl, i) => (
            <td key={i} className="num">
              {formatMoney(amountOf(pnl, key))}
            </td>
          ))}
        </tr>
      ))}
      <tr className="pnl-subtotal">
        <th scope="row">{SECTION_LABEL[section] ?? section} total</th>
        {columns.map((pnl, i) => (
          <td key={i} className="num">
            {formatMoney(pnl.sections[section] ?? '0')}
          </td>
        ))}
      </tr>
    </>
  );
}
